import React, { useRef, useCallback, useEffect } from 'react';
import cx from 'classnames';
import { ReactPhoneInput } from '@@/components/Inputs/PhonePickerInput';

const EditableCell: React.FC<any> = ({
  getValue,
  row,
  column,
  table,
  locked,
  customKey,
}) => {
  const initialValue = getValue();
  const inputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >(null);

  useEffect(() => {
    if (inputRef.current && column.columnDef.meta?.type !== 'checkbox') {
      (inputRef.current as HTMLInputElement | HTMLTextAreaElement).value =
        initialValue || '';
    }
  }, [initialValue, column.columnDef.meta?.type]);

  const updateData = useCallback(() => {
    const currentValue =
      column.columnDef.meta?.type === 'checkbox'
        ? (inputRef.current as HTMLInputElement)?.checked
        : inputRef.current?.value;

    if (currentValue !== undefined) {
      table.options.meta?.updateData(
        table.getRow(row.index).original.id,
        column.id,
        currentValue,
      );
    }
  }, [table, row, column]);

  const onPhoneChange = useCallback(
    (phoneValue: any) => {
      table.options.meta?.updateData(
        table.getRow(row.index).original.id,
        column.id,
        phoneValue,
      );
    },
    [table, row, column],
  );

  switch (column.columnDef.meta?.type) {
    case 'select':
      return (
        <select
          key={table.getRow(row.index).original.id}
          ref={(el) => (inputRef.current = el)}
          defaultValue={initialValue}
          onChange={updateData}
          className="bg-transparent text-sm"
        >
          {column.columnDef.meta?.options?.map(
            (option: { id: string; label: string }) => (
              <option key={option.id} value={option.id} className="text-sm">
                {option.label}
              </option>
            ),
          )}
        </select>
      );

    case 'checkbox':
      return (
        <input
          key={customKey || table.getRow(row.index).original.id}
          ref={(el) => (inputRef.current = el)}
          type="checkbox"
          defaultChecked={locked ? false : Boolean(initialValue)}
          onChange={updateData}
          className="bg-transparent text-sm"
          disabled={!!locked}
        />
      );

    case 'textarea':
      return (
        <textarea
          key={table.getRow(row.index).original.id}
          ref={(el) => (inputRef.current = el)}
          className={cx(
            'bg-gray-100 text-center text-sm rounded-lg p-0.5',
            column.columnDef.meta?.width || 'w-12',
          )}
          defaultValue={initialValue}
          onBlur={updateData}
          rows={column.columnDef.meta?.rows || 3}
        />
      );

    case 'phone':
      return (
        <ReactPhoneInput
          key={table.getRow(row.index).original.id}
          value={initialValue?.internationalNumber}
          onChange={onPhoneChange}
          country={'fr'}
          onlyCountries={['fr']}
          containerStyle={{
            width: '160px',
          }}
          inputStyle={{
            width: '100%',
            height: '32px',
            borderRadius: '4px',
            paddingLeft: 48,
            fontSize: '13px',
          }}
          buttonStyle={{
            borderRadius: '4px 0 0 4px',
          }}
        />
      );
    default:
      return (
        <input
          ref={(el) => (inputRef.current = el)}
          key={table.getRow(row.index).original.id}
          className={cx(
            'bg-transparent text-center text-sm mx-1 my-0.5',
            column.columnDef.meta?.width || 'w-20',
          )}
          defaultValue={initialValue}
          onBlur={updateData}
          type={column.columnDef.meta?.type || 'text'}
        />
      );
  }
};

export default EditableCell;
